import keycloak from '../keycloak';
import {lireFluxSse} from './parseurSse';

/**
 * Flux SSE authentifié par en-tête, résistant à l'expiration du jeton.
 *
 * NOTE — copie conforme de `frontend/src/services/fluxSse.ts`. Les deux applications sont des
 * projets CRA indépendants, sans paquet partagé : le code est dupliqué, mais le mécanisme reste
 * unique par construction. Toute correction ici doit être reportée à l'identique dans l'autre.
 *
 * Ce flux reposait sur `EventSource`, qui ne sait pas porter d'en-tête `Authorization` : le jeton
 * voyageait donc dans l'URL — et se retrouvait dans les journaux d'accès du reverse proxy, dans
 * l'historique du navigateur et dans tout en-tête `Referer`. C'est le défaut corrigé ici.
 *
 * La lecture passe à `fetch` + `ReadableStream`, qui accepte les en-têtes. Le jeton part
 * exclusivement dans `Authorization: Bearer`, et `construireUrl` ne le reçoit plus : la signature
 * elle-même interdit désormais de le glisser dans l'URL par inadvertance.
 *
 * La reprise en main de la reconnexion est CONSERVÉE, pour les deux raisons qui l'avaient motivée
 * et qui n'ont pas disparu :
 *
 * - sur un refus HTTP (401 jeton expiré), rien ne retente de soi-même et la cloche resterait
 *   muette jusqu'au rechargement de la page ;
 * - une reconnexion doit porter le jeton FRAIS, jamais celui qui vient d'échouer.
 *
 * Piège propre à `fetch` : il ne rejette pas sur un statut d'erreur. `response.ok` est donc
 * vérifié explicitement, faute de quoi un 401 passerait pour un flux ouvert dont on attendrait
 * indéfiniment des événements.
 */

let refreshEnCours: Promise<void> | null = null;

/**
 * Rafraîchit le jeton Keycloak s'il expire dans moins de 30 s.
 *
 * Les appels concurrents sont mutualisés : plusieurs erreurs SSE simultanées, ou une erreur SSE
 * concomitante d'un appel REST, ne déclenchent qu'un seul aller-retour vers Keycloak.
 *
 * Rejette si le rafraîchissement échoue — à l'appelant de décider quoi en faire, la conduite à
 * tenir n'étant pas la même pour une requête REST (déconnexion) et pour un flux de fond (arrêt
 * silencieux).
 */
export const rafraichirJeton = (): Promise<void> => {
    if (!keycloak.authenticated) {
        return Promise.resolve();
    }
    if (!refreshEnCours) {
        refreshEnCours = Promise.resolve(keycloak.updateToken(30))
            .then(() => undefined)
            .finally(() => {
                refreshEnCours = null;
            });
    }
    return refreshEnCours;
};

export interface GestionnairesFluxSse {
    /** Écouteurs par nom d'événement serveur. */
    evenements: Record<string, (event: MessageEvent) => void>;
    /** Flux ouvert — appelé à chaque (re)connexion réussie. */
    onOuvert?: () => void;
    /** Flux interrompu, une reconnexion va être tentée. */
    onInterrompu?: () => void;
    /** Reconnexion abandonnée : session expirée ou trop d'échecs consécutifs. */
    onAbandon?: () => void;
}

export interface FluxSse {
    fermer: () => void;
}

/**
 * Au-delà, on cesse de retenter : passé cinq échecs consécutifs, le problème n'est pas le jeton et
 * insister ne ferait que marteler le serveur.
 */
export const TENTATIVES_MAX = 5;

const DELAI_BASE_MS = 1000;
const DELAI_MAX_MS = 30000;

/** Attente avant la n-ième tentative : 1s, 2s, 4s, 8s, 16s, plafonnée à 30s. */
export const delaiAvantTentative = (tentative: number): number =>
    Math.min(DELAI_BASE_MS * 2 ** tentative, DELAI_MAX_MS);

/**
 * Ouvre un flux SSE authentifié.
 *
 * @param construireUrl rend l'URL du flux. Elle ne reçoit AUCUN jeton : c'est délibéré, et c'est
 *                      ce qui garantit qu'aucun appelant ne peut réintroduire le défaut corrigé.
 */
export const ouvrirFluxSse = (
    construireUrl: () => string,
    gestionnaires: GestionnairesFluxSse,
): FluxSse => {
    let controleur: AbortController | null = null;
    let minuterie: ReturnType<typeof setTimeout> | null = null;
    let tentatives = 0;
    let arrete = false;

    const abandonner = () => {
        arrete = true;
        gestionnaires.onAbandon?.();
    };

    const planifierReconnexion = () => {
        if (arrete) {
            return;
        }
        if (tentatives >= TENTATIVES_MAX) {
            abandonner();
            return;
        }
        const delai = delaiAvantTentative(tentatives);
        tentatives += 1;
        minuterie = setTimeout(connecter, delai);
    };

    const connecter = async () => {
        if (arrete) {
            return;
        }
        try {
            await rafraichirJeton();
        } catch {
            // Le rafraîchissement a définitivement échoué : la session est perdue. On s'arrête
            // sans forcer de déconnexion — c'est au prochain appel REST, via son intercepteur,
            // de traiter la session expirée comme il le fait déjà.
            abandonner();
            return;
        }
        if (arrete) {
            return;
        }

        const abort = new AbortController();
        controleur = abort;

        try {
            // Le jeton est lu APRÈS le rafraîchissement : une reconnexion porte donc toujours
            // le jeton frais, jamais celui qui vient d'être refusé.
            const reponse = await fetch(construireUrl(), {
                headers: {
                    Authorization: `Bearer ${keycloak.token ?? ''}`,
                    Accept: 'text/event-stream',
                },
                signal: abort.signal,
            });

            if (!reponse.ok || !reponse.body) {
                throw new Error(`Flux refusé (${reponse.status})`);
            }

            // Remis à zéro seulement sur une connexion réellement établie : un refus n'arrive
            // pas jusqu'ici, donc le compteur continue de courir et la reddition reste garantie.
            tentatives = 0;
            gestionnaires.onOuvert?.();

            await lireFluxSse(reponse.body, (evenement) => {
                const ecouteur = gestionnaires.evenements[evenement.type];
                if (!ecouteur) {
                    return;
                }
                // Les gestionnaires existants sont écrits pour MessageEvent : on leur en fournit
                // un, ce qui évite d'avoir à toucher le moindre appelant.
                ecouteur(new MessageEvent(evenement.type, {
                    data: evenement.data,
                    lastEventId: evenement.lastEventId ?? '',
                }));
            });

            // Le serveur a fermé le flux sans erreur : on reconnecte comme après une coupure.
            if (!arrete) {
                gestionnaires.onInterrompu?.();
                planifierReconnexion();
            }
        } catch {
            if (arrete || abort.signal.aborted) {
                // Fermeture demandée par l'appelant : ce n'est pas une panne.
                return;
            }
            gestionnaires.onInterrompu?.();
            planifierReconnexion();
        } finally {
            if (controleur === abort) {
                controleur = null;
            }
        }
    };

    void connecter();

    return {
        fermer: () => {
            arrete = true;
            if (minuterie) {
                clearTimeout(minuterie);
                minuterie = null;
            }
            controleur?.abort();
            controleur = null;
        },
    };
};
